import React, { forwardRef, useEffect, useState } from 'react';
import {
	computePosition,
	Placement,
	flip,
	shift,
	offset,
	arrow,
} from '@floating-ui/dom';
import './popup.css';
import { onClickAway } from '../../utils/DOM';

export interface IPopupProps {
	anchorQuerySelector: string;
	placement: Placement;
	containerClasses?: string[];
	arrowClasses?: string[];
	openDelay?: number;
	closeDelay?: number;
	noArrow?: boolean;
	open?: boolean;
	onClick?: (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
	onClickAway?: (event: Event) => void;
	id?: string;
	autoPosition?: boolean;
	children?: React.ReactNode;
	offset?: number;
	clickAwayTargets?: Element[];
	style?: React.CSSProperties;
	arrowStyle?: React.CSSProperties;
}

const Popup = forwardRef(
	(props: IPopupProps, ref: React.Ref<HTMLDivElement>) => {
		// This now runs only once per component instance, not on every render.
		const [stableId] = useState(
			() =>
				props.id || 'popup-id--' + Math.random().toString(36).substring(2, 9),
		);

		const {
			containerClasses = [],
			arrowClasses = [],
			noArrow,
			onClick,
			placement,
			autoPosition,
			offset: offsetValue = 10,
			openDelay = 0,
			closeDelay = 0,
			open,
			anchorQuerySelector,
			arrowStyle,
		} = props;

		useEffect(() => {
			// --- Setup DOM Node Queries ---
			const anchor = document.querySelector(
				anchorQuerySelector,
			) as HTMLDivElement;

			// Use the stableId to query for the tooltip and arrow
			const tooltip = document.querySelector('#' + stableId) as HTMLDivElement;
			const arrowElement = document.querySelector(
				'#popup-arrow--' + stableId,
			) as HTMLDivElement;

			// If nodes don't exist yet, do nothing.
			if (!anchor || !tooltip) {
				return;
			}

			// --- Setup Floating UI ---
			const middleware = [offset(offsetValue), shift({ padding: 5 })];

			if (autoPosition === undefined || autoPosition) {
				middleware.push(flip());
			}

			if (!noArrow && arrowElement) {
				middleware.push(arrow({ element: arrowElement }));
			}

			const update = () => {
				computePosition(anchor, tooltip, { placement, middleware }).then(
					({ x, y, placement, middlewareData }: any) => {
						tooltip.style.left = `${x}px`;
						tooltip.style.top = `${y}px`;

						if (!noArrow && arrowElement && middlewareData.arrow) {
							const { x: arrowX, y: arrowY } = middlewareData.arrow;
							const staticSide = (
								{
									top: 'bottom',
									right: 'left',
									bottom: 'top',
									left: 'right',
								} as any
							)[(placement as string).split('-')[0]] as string;

							arrowElement.style.left = arrowX != null ? `${arrowX}px` : '';
							arrowElement.style.top = arrowY != null ? `${arrowY}px` : '';
							(arrowElement.style as any)[staticSide] = '-4px';
						}
					},
				);
			};

			// --- Handler and Timeout Setup ---
			let openTimeout: ReturnType<typeof setTimeout> | null = null;
			let closeTimeout: ReturnType<typeof setTimeout> | null = null;

			const showTooltip = () => {
				if (closeTimeout) clearTimeout(closeTimeout);
				closeTimeout = null;
				openTimeout = setTimeout(() => {
					tooltip.style.display = 'block';
					update();
				}, openDelay);
			};

			const hideTooltip = () => {
				if (openTimeout) clearTimeout(openTimeout);
				openTimeout = null;
				closeTimeout = setTimeout(() => {
					tooltip.style.display = '';
				}, closeDelay);
			};

			const clearClose = () => {
				if (closeTimeout) clearTimeout(closeTimeout);
				closeTimeout = null;
			};

			// --- Logic Branching & Listener Attachment ---
			// Store all added listeners to remove them in the cleanup function
			const activeListeners: {
				target: HTMLElement | Document;
				event: string;
				handler: EventListener;
			}[] = [];

			if (open === true) {
				showTooltip();
			} else if (open === false) {
				hideTooltip();
			} else {
				// Only add hover/focus listeners if `open` is not controlled
				const tooltipListeners = [
					['mouseenter', clearClose as EventListener],
					['mouseleave', hideTooltip as EventListener],
				];

				const anchorListeners = [
					['mouseenter', showTooltip as EventListener],
					['mouseleave', hideTooltip as EventListener],
					['focus', showTooltip as EventListener],
					['blur', hideTooltip as EventListener],
				];

				tooltipListeners.forEach(([event, handler]) => {
					tooltip.addEventListener(
						event as keyof HTMLElementEventMap,
						handler as EventListener,
					);
					activeListeners.push({
						target: tooltip,
						event: event as keyof HTMLElementEventMap,
						handler: handler as EventListener,
					});
				});

				anchorListeners.forEach(([event, handler]) => {
					anchor.addEventListener(
						event as keyof HTMLElementEventMap,
						handler as EventListener,
					);
					activeListeners.push({
						target: anchor,
						event: event as keyof HTMLElementEventMap,
						handler: handler as EventListener,
					});
				});
			}

			let cleanup: () => void = () => {};

			// Click-away listener
			if (!!props.onClickAway) {
				cleanup = onClickAway(
					tooltip,
					props.onClickAway,
					...(props.clickAwayTargets || []),
				);
			}

			return () => {
				// Clear any pending timeouts
				if (openTimeout) clearTimeout(openTimeout);
				if (closeTimeout) clearTimeout(closeTimeout);

				// Run click-away cleanup if it was set
				cleanup();

				// Remove all event listeners this effect added
				activeListeners.forEach(({ target, event, handler }) => {
					target.removeEventListener(event, handler);
				});
			};
		}, [
			anchorQuerySelector,
			placement,
			autoPosition,
			offsetValue,
			noArrow,
			openDelay,
			closeDelay,
			open,
			stableId, // Re-run if our stableId changes (it won't, but it's a dep)
		]);

		return (
			<div
				ref={ref} // Pass the forwardedRef to the DOM element
				className={['popup', ...containerClasses].join(' ')}
				onClick={onClick}
				id={stableId} // Use the stable ID
				style={props.style}
			>
				{props.children}
				{!noArrow && (
					<div
						id={`popup-arrow--${stableId}`} // Use the stable ID
						className={['popup-arrow', ...arrowClasses].join(' ')}
						style={arrowStyle}
					/>
				)}
			</div>
		);
	},
);

export default Popup;
